import { apiClient } from "@/lib/api/client";
import type { IFieldValue } from "@speakai/shared";

// -- Types -----------------------------------------------------------

export interface Field {
  _id?: string;
  id: string;
  fieldId?: string;
  name: string;
  description: string;
  type: string;
  isActive: boolean;
  isDeleted?: boolean;
  privacyMode: string;
  prompt?: string;
  notApplicableValues?: string;
  allowedValues?: string[];
  allowedValuesMode?: "single" | "multiple";
  otherValues?: boolean;
  options?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface FieldFilters {
  filterName?: string;
  isActive?: string;
  privacyMode?: string;
}

export interface FieldWritePayload {
  name: string;
  type: string;
  description?: string;
  prompt?: string;
  notApplicableValues?: string;
  allowedValues?: string[];
  allowedValuesMode?: "single" | "multiple";
  otherValues?: boolean;
  privacyMode?: string;
}

export interface SaveFieldValuesPayload {
  mediaIds?: string[];
  fields: Array<{ id: string; value: string }>;
}

export type SaveFieldValuesByEntityPayload =
  | { fields: IFieldValue[]; folderId: string }
  | { fields: IFieldValue[]; mediaIds: string[] };

export interface GeneratedField {
  name: string;
  type: string;
}

export interface GenerateFieldsParams {
  sourceType: string;
  /** Required by the server (Joi). Caller must validate non-empty before invoking. */
  description: string;
}

// -- API Functions ---------------------------------------------------

export async function getFields(filters?: FieldFilters): Promise<Field[]> {
  const params = new URLSearchParams();
  if (filters?.filterName) params.set("filterName", filters.filterName);
  const isActive = filters?.isActive ?? "true";
  if (isActive !== "all") params.set("isActive", isActive);
  if (filters?.privacyMode && filters.privacyMode !== "all")
    params.set("privacyMode", filters.privacyMode);

  const query = params.toString();
  const res = await apiClient.get<Field[]>(`/fields${query ? `?${query}` : ""}`);
  if (!res.success) throw new Error(res.error ?? "Failed to fetch fields");
  // Some legacy responses wrap the array in { data: Field[] } — normalize.
  const raw = Array.isArray(res.data)
    ? res.data
    : (res.data as unknown as { data?: Field[] })?.data ?? [];
  return raw;
}

export async function createField(payload: FieldWritePayload): Promise<Field> {
  const res = await apiClient.post<Field>("/fields", payload);
  if (!res.success || !res.data)
    throw new Error(res.error ?? "Failed to create field");
  return res.data;
}

export async function updateField(
  id: string,
  payload: FieldWritePayload
): Promise<Field> {
  const res = await apiClient.put<Field>(`/fields/${id}`, payload);
  if (!res.success) throw new Error(res.error ?? "Failed to update field");
  return res.data!;
}

export async function bulkUpdateFieldStatus(
  fieldIds: string[],
  isActive: boolean
): Promise<{ status: string }> {
  const res = await apiClient.put<{ status: string }>("/fields/status", {
    fieldIds,
    isActive,
  });
  if (!res.success)
    throw new Error(res.error ?? "Failed to update field status");
  return res.data ?? { status: "ok" };
}

export async function saveFieldValues(
  payload: SaveFieldValuesPayload
): Promise<{ message?: string }> {
  const res = await apiClient.post<{ message?: string }>(
    "/fields/batch",
    payload
  );
  if (!res.success) throw new Error(res.error ?? "Failed to save field values");
  return res.data ?? {};
}

export async function saveFieldValuesByEntity(
  payload: SaveFieldValuesByEntityPayload
): Promise<unknown> {
  const res = await apiClient.post("/fields/fieldvalues", payload);
  if (!res.success) throw new Error(res.error ?? "Failed to save field values");
  return res.data;
}

export async function generateFields(
  params: GenerateFieldsParams
): Promise<GeneratedField[]> {
  const res = await apiClient.post<GeneratedField[]>(
    "/fields/generate",
    params
  );
  if (!res.success) throw new Error(res.error ?? "Failed to generate fields");
  return res.data!;
}

export async function syncRecorderAnswersToField(data: {
  recorderId: string;
  questionId: string;
}) {
  const res = await apiClient.post<{ status: string; message: string }>(
    "/fields/sync-recorder-answers",
    data
  );
  if (!res.success)
    throw new Error(res.error ?? "Failed to sync answers to field");
  return res.data!;
}
